import type { NoteTiming, TimingScore } from '../melody/score-timing.js'

/**
 * The map from a `scoreTiming` verdict to the §10 lexicon entries that describe it.
 *
 * The sibling of `pitch-feedback.ts`, and it exists for the same reason: the Melody Trainer's
 * output is a measurement of the user, and a surface that assembled its own prose could tell a
 * musician something no one wrote and no one reviewed. Every string they read about their timing
 * is one locked §10 entry, chosen here, from numbers computed in `score-timing.ts`.
 *
 * Pure and total. `null` in - nothing the aligner paired - is `null` out: a first-class outcome,
 * not an error and not a timing of zero.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6.2, §6.3, §10
 */

/**
 * The correlation at which "you caught the feel" is the truer sentence.
 *
 * A CONSTANT THE SPEC DID NOT GIVE. §6.2 defines the feel match and §10 authored the two
 * sentences, but no boundary between them was set. 0.5 is where the offsets the player produced
 * share a quarter of their variance with the melody's - "more like the melody's push and pull than
 * not". It is deliberately not near 1: §10's copy rules say `timing-feel-straight` is never a
 * judgement, so being told you played it straight costs a player nothing, while an unearned "you
 * caught the feel" is the failure mode that matters.
 *
 * Flagged for Bowie to set against fixtures, as §6.2 requires of every threshold in this tool.
 */
export const feelMatchedR = 0.5

export interface TimingNoteFeedback {
  /** One of the five §6.2 band entries. */
  bandLexiconId: string
  /** Unsigned whole milliseconds, for the entry's `{{ms}}`. The direction is in the id. */
  ms: number
}

export interface TimingFeedback {
  perNote: TimingNoteFeedback[]
  /**
   * `timing-rushing`, `timing-dragging` or `timing-even` - or null when every paired note was
   * displaced and there is no lean to name.
   */
  tendencyLexiconId: string | null
  /** Unsigned whole milliseconds of the mean, for the tendency entry's `{{ms}}`. Null with it. */
  tendencyMs: number | null
  /** A feel entry, or null when the reference had no feel to match (§6.2). */
  feelLexiconId: string | null
  /** The correlation to two decimals, for `{{r}}`. Null whenever `feelLexiconId` is. */
  feelR: number | null
}

const bandLexiconId = (note: NoteTiming): string => {
  if (note.band === 'wrong-place') {
    /*
     * No direction either, and for the opposite reason to `on`. The note did not arrive early or
     * late; it arrived somewhere else. "400 ms early" invites a player to shave 400 ms off a note
     * that belongs on a different beat entirely.
     */
    return 'timing-wrong-place'
  }
  if (note.band === 'on') {
    /*
     * No direction inside the fusion threshold. §6.2 puts "on" below the point where two onsets
     * are heard as separate events, so "8 ms early" is not a thing a player can hear or act on -
     * and printing it would invite them to correct something that is not there.
     */
    return 'timing-on'
  }
  const direction = note.errorMs < 0 ? 'early' : 'late'
  return `timing-${note.band}-${direction}`
}

export const toTimingFeedback = (score: TimingScore | null): TimingFeedback | null => {
  if (score === null) {
    return null
  }

  const feelLexiconId =
    score.feelMatch === undefined ? null : score.feelMatch >= feelMatchedR ? 'timing-feel-matched' : 'timing-feel-straight'

  return {
    perNote: score.perNote.map(note => ({ bandLexiconId: bandLexiconId(note), ms: Math.round(Math.abs(note.errorMs)) })),
    tendencyLexiconId: score.tendency === null ? null : `timing-${score.tendency}`,
    tendencyMs: score.meanErrorMs === null ? null : Math.round(Math.abs(score.meanErrorMs)),
    feelLexiconId,
    /** Two decimals, because that is the precision `{{r}}`'s copy was written for. */
    feelR: score.feelMatch === undefined ? null : Math.round(score.feelMatch * 100) / 100,
  }
}

/**
 * Which sentence the prerequisite gate shows.
 *
 * `0` is a measurement, not an absence - a device really can come back at the resolution floor -
 * so the check is against `null` and never against falsiness.
 */
export const latencyCalibrationLexiconId = (inputLatencyMs: number | null): string =>
  inputLatencyMs === null ? 'timing-calibrate-first' : 'timing-calibrated'
